const LAST_ALLOWED_KEY = 'fattah:lastAllowedPath';

const SKIP_RECORD_PREFIXES = ['/master/login', '/pos/login', '/crm/login', '/hr/login', '/desk/login'];

export type RevertPathOptions = {
  /** When provided, candidate paths must pass this check. */
  canAccess?: (pathname: string) => boolean;
  /** Extra candidates tried after last-allowed and fallback (in order). */
  extraCandidates?: string[];
};

function pathOnly(full: string): string {
  return full.split('?')[0] ?? full;
}

function isUsableRevertCandidate(
  candidateFull: string,
  blockedPathname: string,
  canAccess?: (pathname: string) => boolean,
): boolean {
  const candidate = pathOnly(candidateFull);
  const blocked = pathOnly(blockedPathname);
  if (!candidate || candidate === blocked) return false;
  if (canAccess && !canAccess(candidate)) return false;
  return true;
}

/** Revert URL to last good page, or fallback when user hits a blocked/unknown route. */
export function getRevertPath(blockedPathname: string, fallback: string): string {
  return resolveSafeRevertPath(blockedPathname, fallback) ?? fallback;
}

/**
 * Pick a safe URL to restore after access denial.
 * Returns null when no candidate is accessible — caller should stay on the current URL.
 */
export function resolveSafeRevertPath(
  blockedPathname: string,
  fallback: string,
  options?: RevertPathOptions,
): string | null {
  const canAccess = options?.canAccess;
  const candidates: string[] = [];

  const last = getLastAllowedPath();
  if (last) candidates.push(last);

  candidates.push(fallback);

  if (options?.extraCandidates?.length) {
    candidates.push(...options.extraCandidates);
  }

  for (const candidate of candidates) {
    if (isUsableRevertCandidate(candidate, blockedPathname, canAccess)) {
      return candidate;
    }
  }

  return null;
}

/** True when the POS billing terminal layout should mount (not org workspace). */
export function isPosTerminalPath(pathname: string): boolean {
  return pathname === '/pos' || pathname.startsWith('/pos/');
}

function shouldRecordPath(pathname: string): boolean {
  if (!pathname || pathname === '/') return false;
  if (SKIP_RECORD_PREFIXES.some((p) => pathname.startsWith(p))) return false;
  return true;
}

export function recordAllowedPath(pathname: string, search = ''): void {
  if (!shouldRecordPath(pathname)) return;
  const full = `${pathname}${search}`;
  sessionStorage.setItem(LAST_ALLOWED_KEY, full);
}

export function getLastAllowedPath(): string | null {
  return sessionStorage.getItem(LAST_ALLOWED_KEY);
}

export function seedLastAllowedPath(path: string): void {
  recordAllowedPath(path.split('?')[0] ?? path, path.includes('?') ? path.slice(path.indexOf('?')) : '');
}

export function clearLastAllowedPath(): void {
  sessionStorage.removeItem(LAST_ALLOWED_KEY);
}
